// eBay Smart Link Configuration
export const EBAY_SMART_LINK_CONFIG = {
  // Your eBay Campaign ID
  CAMPAIGN_ID: '5339120245',
  
  // Smart Link Script URL
  SMART_TOOLS_URL: 'https://epnt.ebay.com/static/epn-smart-tools.js',
  
  // Track initialization state
  isInitialized: false,
  isInitializing: false,
  initializationPromise: null,
  
  // Initialize the smart link with proper error handling and retry
  initializeSmartLink() {
    // Return existing promise if already initializing
    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    
    // Return resolved promise if already initialized
    if (this.isInitialized) {
      return Promise.resolve();
    }
    
    this.isInitializing = true;
    
    this.initializationPromise = new Promise((resolve, reject) => {
      try {
        // Set the campaign ID
        window._epn = { 
          campaign: this.CAMPAIGN_ID,
          onLoad: () => {
            console.log('[Smart Link] Script loaded successfully');
            this.isInitialized = true;
            this.isInitializing = false;
            resolve();
          },
          onError: (error) => {
            console.warn('[Smart Link] Script failed to load:', error);
            this.isInitialized = false;
            this.isInitializing = false;
            resolve(); // Still resolve to allow fallback usage
          }
        };
        
        // Check if script already exists
        const existingScript = document.querySelector(`script[src="${this.SMART_TOOLS_URL}"]`);
        if (existingScript) {
          console.log('[Smart Link] Script already exists, checking if loaded');
          if (window._epn && window._epn.smartLink) {
            this.isInitialized = true;
            this.isInitializing = false;
            resolve();
          } else {
            // Script exists but not loaded yet, wait for it
            setTimeout(() => {
              if (window._epn && window._epn.smartLink) {
                this.isInitialized = true;
                this.isInitializing = false;
                resolve();
              } else {
                this.isInitialized = false;
                this.isInitializing = false;
                resolve(); // Allow fallback
              }
            }, 2000);
          }
          return;
        }
        
        // Load the smart tools script
        const script = document.createElement('script');
        script.src = this.SMART_TOOLS_URL;
        script.async = true;
        script.onload = () => {
          console.log('[Smart Link] Script loaded successfully');
          this.isInitialized = true;
          this.isInitializing = false;
          resolve();
        };
        script.onerror = (error) => {
          console.warn('[Smart Link] Script failed to load:', error);
          this.isInitialized = false;
          this.isInitializing = false;
          resolve(); // Still resolve to allow fallback usage
        };
        
        document.head.appendChild(script);
        console.log('[Smart Link] Initializing with campaign ID:', this.CAMPAIGN_ID);
        
        // Timeout fallback
        setTimeout(() => {
          if (!this.isInitialized) {
            console.warn('[Smart Link] Initialization timeout, using fallback');
            this.isInitialized = false;
            this.isInitializing = false;
            resolve();
          }
        }, 5000);
        
      } catch (error) {
        console.error('[Smart Link] Initialization error:', error);
        this.isInitialized = false;
        this.isInitializing = false;
        resolve(); // Still resolve to allow fallback usage
      }
    });
    
    return this.initializationPromise;
  },
  
  // Generate smart link URL for a specific item with improved fallback
  generateSmartLink(itemId, itemWebUrl = null) {
    if (!itemId) {
      console.warn('[Smart Link] No item ID provided');
      return itemWebUrl || '#';
    }
    
    // Validate itemId format (should be numeric)
    if (!/^\d+$/.test(itemId)) {
      console.warn('[Smart Link] Invalid item ID format:', itemId);
      return this.createFallbackUrl(itemId, itemWebUrl);
    }
    
    // Temporarily disable smart link API due to malformed URL issues
    // TODO: Investigate why smart link API returns malformed URLs like v1%7C197677007696%7C0
    console.log('[Smart Link] Smart Link API temporarily disabled, using fallback method');
    
    // Check if Smart Link is available and initialized (disabled for now)
    if (false && this.isInitialized && window._epn && window._epn.smartLink) {
      try {
        console.log('[Smart Link] Using Smart Link for item:', itemId);
        const smartLinkUrl = window._epn.smartLink(itemId);
        
        // Validate the returned URL
        if (this.isValidEbayUrl(smartLinkUrl)) {
          console.log('[Smart Link] Generated valid URL:', smartLinkUrl);
          return smartLinkUrl;
        } else {
          console.warn('[Smart Link] Smart Link returned invalid URL:', smartLinkUrl);
          // Fall through to fallback
        }
      } catch (error) {
        console.warn('[Smart Link] Smart Link generation failed:', error);
        // Fall through to fallback
      }
    }
    
    // Use fallback URL creation
    return this.createFallbackUrl(itemId, itemWebUrl);
  },
  
  // Create fallback URL with proper validation
  createFallbackUrl(itemId, itemWebUrl = null) {
    console.log('[Smart Link] Using fallback URL for item:', itemId);
    
    // If we have the original itemWebUrl, use it as base and add tracking parameters
    if (itemWebUrl && itemWebUrl !== '#' && this.isValidEbayUrl(itemWebUrl)) {
      try {
        const url = new URL(itemWebUrl);
        url.searchParams.set('campid', this.CAMPAIGN_ID);
        url.searchParams.set('toolid', '10001');
        url.searchParams.set('customid', 'goatcards');
        url.searchParams.set('mkevt', '1');
        url.searchParams.set('mkcid', '1');
        url.searchParams.set('mkrid', '711-53200-19255-0');
        console.log('[Smart Link] Using itemWebUrl with tracking:', url.toString());
        return url.toString();
      } catch (error) {
        console.warn('[Smart Link] Failed to parse itemWebUrl, using basic fallback:', error);
      }
    }
    
    // Basic fallback URL
    const fallbackUrl = `https://www.ebay.com/itm/${itemId}?campid=${this.CAMPAIGN_ID}&toolid=10001&customid=goatcards&mkevt=1&mkcid=1&mkrid=711-53200-19255-0`;
    console.log('[Smart Link] Using basic fallback URL:', fallbackUrl);
    return fallbackUrl;
  },
  
  // Validate if URL is a proper eBay URL
  isValidEbayUrl(url) {
    if (!url || typeof url !== 'string') return false;
    
    try {
      const parsedUrl = new URL(url);
      const isValidEbay = parsedUrl.hostname.includes('ebay.com') && 
                         parsedUrl.pathname.includes('/itm/') &&
                         !url.includes('v1%7C') && // Exclude malformed smart link URLs
                         !url.includes('v1|'); // Exclude malformed smart link URLs
      
      console.log('[Smart Link] URL validation:', { url, isValidEbay });
      return isValidEbay;
    } catch (error) {
      console.warn('[Smart Link] URL validation failed:', error);
      return false;
    }
  },
  
  // Check if smart link is ready
  isReady() {
    return this.isInitialized && window._epn && window._epn.smartLink;
  },
  
  // Get initialization status
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isInitializing: this.isInitializing,
      isReady: this.isReady()
    };
  }
}; 